import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";

const blank = { id: null, name: "", description: "", rows: [{ min: 0, max: null, rate: 0 }] };

function Editor({ ts, onSaved, onCancel }) {
  const [f, setF] = useState(ts);
  const [warn, setWarn] = useState([]);
  const [busy, setBusy] = useState(false);
  const rows = f.rows;
  const setRow = (i, k) => (e) => setF({ ...f, rows: rows.map((r, j) => (j === i ? { ...r, [k]: e.target.value === "" ? (k === "max" ? null : 0) : Number(e.target.value) } : r)) });
  const body = { name: f.name, description: f.description, rows };
  useEffect(() => {
    const t = setTimeout(() => api.post("/prim/tier-sets/validate", body).then(({ data }) => setWarn(data.warnings)).catch((e) => setWarn([apiError(e)])), 300);
    return () => clearTimeout(t);
  }, [JSON.stringify(rows)]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try { f.id ? await api.put(`/prim/tier-sets/${f.id}`, body) : await api.post("/prim/tier-sets", body); toast.success("Kademe tablosu kaydedildi; yalnızca bundan sonraki hesaplamalarda kullanılır"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="panel p-3 space-y-2 border-blue-300" data-testid="tierset-editor">
      <div className="flex gap-2"><Input placeholder="Tablo adı" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="tierset-name" />
        <Input placeholder="Açıklama" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <table className="erp-table text-[12px]"><thead><tr><th>Min. marj % (dahil)</th><th>Maks. marj % (hariç, boş = sınırsız)</th><th>Prim oranı %</th><th></th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} data-testid={`tierset-row-${i}`}>
            <td><Input type="number" step="0.01" className="h-8" value={r.min} onChange={setRow(i, "min")} data-testid={`tierset-min-${i}`} /></td>
            <td><Input type="number" step="0.01" className="h-8" value={r.max ?? ""} onChange={setRow(i, "max")} data-testid={`tierset-max-${i}`} /></td>
            <td><Input type="number" step="0.01" className="h-8" value={r.rate} onChange={setRow(i, "rate")} data-testid={`tierset-rate-${i}`} /></td>
            <td><Button size="sm" variant="ghost" onClick={() => setF({ ...f, rows: rows.filter((_, j) => j !== i) })} disabled={rows.length === 1}><Trash2 className="h-3.5 w-3.5" /></Button></td>
          </tr>))}</tbody></table>
      {warn.length > 0 && <div className="text-[12px] text-amber-800 bg-amber-50 rounded p-2" data-testid="tierset-warnings">{warn.map((w) => <div key={w}>⚠ {w}</div>)}</div>}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => { const last = rows[rows.length - 1]; setF({ ...f, rows: [...rows.slice(0, -1), { ...last, max: last.max ?? last.min + 5 }, { min: last.max ?? last.min + 5, max: null, rate: last.rate }] }); }} data-testid="tierset-add-row"><Plus className="h-3.5 w-3.5 mr-1" />Satır</Button>
        <Button size="sm" disabled={busy} onClick={save} data-testid="tierset-save">Kaydet</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Vazgeç</Button>
      </div>
    </div>
  );
}

export default function TierSets({ sets, onChanged }) {
  const [edit, setEdit] = useState(null);
  return (
    <div className="space-y-2" data-testid="tier-sets">
      <div className="flex items-center justify-between"><div className="text-[13px] font-semibold">Kademe Tabloları (Kâr Marjı → Prim Oranı)</div>
        <Button size="sm" variant="outline" onClick={() => setEdit(blank)} data-testid="tierset-new"><Plus className="h-4 w-4 mr-1" />Yeni Tablo</Button></div>
      {edit && <Editor ts={edit} onSaved={() => { setEdit(null); onChanged(); }} onCancel={() => setEdit(null)} />}
      <div className="grid md:grid-cols-2 gap-3">{sets.map((s) => (
        <div key={s.id} className="panel p-3 text-[12px]" data-testid={`tierset-card-${s.id}`}>
          <div className="flex justify-between"><div><b>{s.name}</b> {s.is_default && <span className="text-[10px] text-blue-700">varsayılan</span>} <span className="text-slate-400">v{s.version} · {s.assigned} personel</span></div>
            <Button size="sm" variant="ghost" onClick={() => setEdit(s)} data-testid={`tierset-edit-${s.id}`}>Düzenle</Button></div>
          <div className="text-slate-500 mb-1">{s.description}</div>
          {s.rows.map((r, i) => <div key={i} className="font-mono">%{r.min} – {r.max == null ? "∞" : `%${r.max}`} → <b>%{r.rate}</b></div>)}
          {s.warnings.length > 0 && <div className="text-amber-700 mt-1">{s.warnings.join(" · ")}</div>}
          {s.updated_at && <div className="text-slate-400 mt-1">Son değişiklik {formatDate(s.updated_at)} · {s.updated_by_name}</div>}
        </div>))}</div>
    </div>
  );
}
